import React, { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2, Bug, Users, Clock, Layers, Bell, SlidersHorizontal, Zap } from 'lucide-react';

const fmt = (d) => (d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');

export default function FrontendErrors() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showResolved, setShowResolved] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [config, setConfig] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (!showResolved) params.resolved = false;
      const [g, a] = await Promise.all([
        api.get('/admin/frontend-errors/grouped', { params }),
        api.get('/admin/frontend-errors/alerts').catch(() => ({ data: { alerts: [] } })),
      ]);
      setData(g.data);
      setAlerts(a.data.alerts || []);
    } catch (_) {
      setData({ groups: [], summary: {} });
    } finally { setLoading(false); }
  }, [showResolved]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/admin/frontend-errors/alert-config').then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const ackAlert = async (id) => {
    try { await api.patch(`/admin/frontend-errors/alerts/${id}/ack`); load(); } catch (_) { /* noop */ }
  };
  const saveConfig = async () => {
    setSavingConfig(true);
    try { await api.put('/admin/frontend-errors/alert-config', config); setShowConfig(false); load(); }
    catch (_) { /* noop */ } finally { setSavingConfig(false); }
  };
  const setCfg = (k, v) => setConfig((c) => ({ ...c, [k]: v }));

  const resolveGroup = async (g) => {
    try {
      await api.patch('/admin/frontend-errors/resolve-group', { context: g.context, message: g.message });
      load();
    } catch (_) { /* noop */ }
  };

  const s = data?.summary || {};
  const stat = (label, value, tint) => (
    <div className={`rounded-xl border p-3 ${tint}`} data-testid={`fe-stat-${label}`}>
      <div className="text-2xl font-bold">{value ?? 0}</div>
      <div className="text-[11px] uppercase tracking-wide opacity-80">{label}</div>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6" data-testid="admin-frontend-errors">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <Bug className="w-5 h-5 text-rose-600" /> Frontend Errors
        </h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowConfig((v) => !v)} data-testid="alert-config-toggle">
            <SlidersHorizontal className="w-4 h-4 mr-1.5" /> Thresholds
          </Button>
          <Button variant={showResolved ? 'default' : 'outline'} size="sm" onClick={() => setShowResolved((v) => !v)} data-testid="toggle-resolved">
            {showResolved ? 'Showing all' : 'Open only'}
          </Button>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="frontend-errors-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {showConfig && config && (
        <Card className="mb-4 border-indigo-200" data-testid="alert-config-panel">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Bell className="w-4 h-4" /> Spike alert thresholds</span>
              <label className="text-xs flex items-center gap-2">
                <input type="checkbox" checked={config.enabled} onChange={(e) => setCfg('enabled', e.target.checked)} data-testid="cfg-enabled" />
                Alerting enabled
              </label>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                ['occurrence_threshold', 'High-volume count'],
                ['multi_user_threshold', 'Distinct users'],
                ['new_build_min', 'Post-build min count'],
                ['window_minutes', 'Window (min)'],
                ['new_build_window_minutes', 'New-build window (min)'],
                ['cooldown_minutes', 'Cooldown (min)'],
              ].map(([k, label]) => (
                <label key={k} className="text-xs text-slate-600">
                  {label}
                  <Input type="number" value={config[k]} onChange={(e) => setCfg(k, parseInt(e.target.value || '0', 10))} className="h-8 mt-1" data-testid={`cfg-${k}`} />
                </label>
              ))}
            </div>
            <div className="mt-3 flex justify-end">
              <Button size="sm" onClick={saveConfig} disabled={savingConfig} data-testid="cfg-save">
                {savingConfig ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save thresholds'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {alerts.length > 0 && (
        <div className="mb-4 space-y-2" data-testid="active-alerts">
          {alerts.map((al) => (
            <div key={al.id} className="flex items-start gap-3 rounded-xl border border-rose-300 bg-rose-50 p-3" data-testid={`alert-${al.id}`}>
              <Zap className="w-4 h-4 text-rose-600 mt-0.5 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-rose-600 text-white border-0 text-[10px]">{al.alert_label}</Badge>
                  <Badge className="bg-slate-800 text-white border-0 text-[10px] font-mono">{al.context}</Badge>
                  <span className="text-xs text-rose-700 font-semibold">{al.occurrences}× · {al.affected_users} user{al.affected_users === 1 ? '' : 's'}</span>
                  {al.build && <Badge className="bg-cyan-50 text-cyan-700 border border-cyan-200 text-[10px]">build {al.build}</Badge>}
                </div>
                <p className="text-sm text-slate-800 mt-1 break-words">{al.message || '—'}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => ackAlert(al.id)} className="flex-shrink-0" data-testid={`ack-alert-${al.id}`}>
                Acknowledge
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {stat('open events', s.open, 'border-rose-200 bg-rose-50 text-rose-700')}
        {stat('signatures', s.signatures, 'border-indigo-200 bg-indigo-50 text-indigo-700')}
        {stat('last 24h', s.last_24h, 'border-amber-200 bg-amber-50 text-amber-700')}
        {stat('all time', s.total, 'border-slate-200 bg-slate-50 text-slate-700')}
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>
          ) : (data?.groups || []).length === 0 ? (
            <div className="text-center py-12 text-slate-500 text-sm flex flex-col items-center gap-2" data-testid="frontend-errors-empty">
              <CheckCircle2 className="w-8 h-8 text-emerald-500" />
              No {showResolved ? '' : 'open '}frontend errors. All clear.
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {data.groups.map((g, idx) => (
                <div key={`${g.context}-${idx}`} className="p-4" data-testid={`error-group-${idx}`}>
                  <div className="flex items-start gap-3">
                    <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${g.open_count > 0 ? 'text-rose-500' : 'text-slate-300'}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className="bg-slate-800 text-white border-0 text-[10px] font-mono">{g.context}</Badge>
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-rose-700" data-testid={`group-count-${idx}`}>
                          <Layers className="w-3 h-3" /> {g.occurrences}×
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs text-slate-600" title="affected users">
                          <Users className="w-3 h-3" /> {g.affected_users}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs text-slate-400" title="most recent">
                          <Clock className="w-3 h-3" /> {fmt(g.last_seen)}
                        </span>
                        {g.open_count === 0 && <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">resolved</Badge>}
                      </div>
                      <p className="text-sm text-slate-800 mt-1 break-words">{g.message || '—'}</p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {(g.roles || []).map((r) => <Badge key={r} variant="outline" className="text-[10px]">{r}</Badge>)}
                        {(g.builds || []).map((b) => <Badge key={b} className="bg-cyan-50 text-cyan-700 border border-cyan-200 text-[10px]" title="build / release">build {b}</Badge>)}
                      </div>
                      {(g.paths || []).length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`group-paths-${idx}`}>
                          {g.paths.map((p) => <span key={p} className="text-[11px] font-mono text-slate-500 bg-slate-50 rounded px-1.5 py-0.5">{p}</span>)}
                        </div>
                      )}
                      <div className="text-[11px] text-slate-400 mt-1">First seen {fmt(g.first_seen)}</div>
                    </div>
                    {g.open_count > 0 && (
                      <Button size="sm" variant="outline" onClick={() => resolveGroup(g)} className="flex-shrink-0" data-testid={`resolve-group-${idx}`}>
                        Resolve {g.open_count > 1 ? `(${g.open_count})` : ''}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
